import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Avatar, Chip, Stack,
  Card, CardContent, Button, IconButton, Skeleton, Alert, CircularProgress,
  Snackbar, useTheme, useMediaQuery, CardMedia
} from '@mui/material';
import {
  LocationOn, Phone, Favorite, FavoriteBorder, Star,
  Navigation, Store
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { url } from '../constants';

// ✅ Helper to fix the missing image path and link it to the backend server
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('data:image') || imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${url}${imagePath}`;
};

const VendorDetails = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ---------------- STATE ----------------
  const [vendorData, setVendorData] = useState(null);
  const [dealsWithDistance, setDealsWithDistance] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isLoggedIn = !!localStorage.getItem('access_token');

  const openInGoogleMaps = () => {
    if (!vendorData?.location?.coordinates || vendorData.location.coordinates.length !== 2) {
      return;
    }
    const [lng, lat] = vendorData.location.coordinates;
    const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(mapsUrl, '_blank');
  };

  // ---------------- DISTANCE ----------------
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const toRad = (v) => (v * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const getUserLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  // ---------------- FAVORITE - EXACT DealsNearYou LOGIC ----------------
  const handleToggleFavorite = async (dealId, e) => {
    e.stopPropagation();
    
    if (!isLoggedIn) {
      setSnackbar({
        open: true,
        message: 'Please login to save favorites',
        severity: 'warning'
      });
      setTimeout(() => navigate('/login'), 1500);
      return;
    }

    const isFav = favorites.includes(dealId);
    setFavoritesLoading(prev => ({ ...prev, [dealId]: true }));
    
    let updatedFavorites;
    if (isFav) {
      updatedFavorites = favorites.filter(fav => fav !== dealId);
    } else {
      updatedFavorites = [...favorites, dealId];
    }
    
    setFavorites(updatedFavorites);
    localStorage.setItem('userFavorites', JSON.stringify(updatedFavorites));

    try {
      const token = localStorage.getItem('access_token');
      const endpoint = isFav 
        ? `${url}/deals/remove-favorite` 
        : `${url}/deals/add-favorite`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ deal_id: dealId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update favorites');
      }

      setSnackbar({
        open: true,
        message: isFav ? 'Removed from favorites' : 'Added to favorites',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error updating favorites:', error);
      setFavorites(favorites);
      localStorage.setItem('userFavorites', JSON.stringify(favorites));
      
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update favorites',
        severity: 'error'
      });
    } finally {
      setFavoritesLoading(prev => {
        const newState = { ...prev };
        delete newState[dealId];
        return newState;
      });
    }
  };

  const isFavorite = (dealId) => favorites.includes(dealId);

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('userFavorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // ---------------- FETCH ----------------
  useEffect(() => {
    const fetchVendorData = async () => {
      try {
        setLoading(true);
        setError(null);

        // ✅ 1. Fetch vendor data FIRST so the page renders immediately
        const response = await fetch(
          `${url}/deals/vendor-and-deals/${vendorId}`
        );

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();
        setVendorData(data.vendor || data);

        // ✅ 2. Format deals WITHOUT distance initially
        const initialDeals = (data.deals || []).map((deal) => ({
          ...deal,
          id: deal._id || deal.id
        }));

        setDealsWithDistance(initialDeals);
        setLoading(false); // 🛑 Stop loading here so the user sees the page instantly!

        // ✅ 3. Ask for location in the background
        const userLoc = await getUserLocation();
        
        // ✅ 4. If they allow location, calculate distances and update silently
        if (userLoc) {
          setUserLocation(userLoc);
          
          const processedDeals = initialDeals.map((deal) => {
            const coords = deal.location?.coordinates || data.vendor?.location?.coordinates;
            
            if (coords && coords.length === 2) {
              const [lng, lat] = coords;
              return {
                ...deal,
                distance: calculateDistance(
                  userLoc.lat,
                  userLoc.lng,
                  lat,
                  lng
                ).toFixed(1)
              };
            }
            return deal;
          }).sort(
            (a, b) => (parseFloat(a.distance) || 999) - (parseFloat(b.distance) || 999)
          );

          setDealsWithDistance(processedDeals);
        }

      } catch (err) {
        setError(err.message || 'Failed to load vendor details');
        setLoading(false);
      }
    };

    if (vendorId) fetchVendorData();
  }, [vendorId]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ---------------- LOADING ----------------
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={3}>
          {[...Array(8)].map((_, i) => (
            <Grid item xs={6} md={4} key={i}>
              <Skeleton height={300} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  // ---------------- ERROR ----------------
  if (error || !vendorData) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">
          {error || 'Vendor not found'}
        </Alert>
      </Container>
    );
  }

  // ================= RENDER =================
  return (
    <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">

        {/* ================= VENDOR HEADER ================= */}
        <Paper
          sx={{
            p: 4,
            mb: 6,
            borderRadius: 3,
            border: '1px solid #E2E8F0',
            background:
              'linear-gradient(135deg, #FFFFFF 0%, #F1F5F9 100%)'
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2} mb={3}>
            <Box>
              <Typography variant="h4" fontWeight={800}>
                {vendorData.business_name}
              </Typography>
              <Typography color="text.secondary">
                {dealsWithDistance.length} Active Deals
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Avatar
                src={getImageUrl(vendorData.profile_image)} 
                sx={{
                  width: 80,
                  height: 80,
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  bgcolor: 'linear-gradient(135deg, #1a7f3a 0%, #0f5328 100%)',
                  mb: 2,
                  border: vendorData.profile_image ? '2px solid #1a7f3a' : 'none'
                }}
              >
                {vendorData.business_name?.charAt(0)?.toUpperCase()}
              </Avatar>

              {vendorData.description && (
                <Typography color="text.secondary">
                  {vendorData.description}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3 }}>
              {vendorData.phone && (
                <Stack direction="row" spacing={2} mb={2} alignItems="center">
                  <Phone sx={{ color: '#1a7f3a' }} />
                  <Typography
                    component="a"
                    href={`tel:${vendorData.phone}`}
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      fontWeight: 600,
                      '&:hover': { color: '#0f5328' },
                      cursor: 'pointer'
                    }}
                  >
                    Call Now
                  </Typography>
                </Stack>
              )}

                {vendorData.address && (
                  <Stack direction="row" spacing={2} mb={2}>
                    <LocationOn />
                    <Typography>
                    {vendorData.address.street},{vendorData.address.city}, {vendorData.address.state}
                    </Typography>
                  </Stack>
                )}

                {/* GOOGLE MAP BUTTON */}
                {vendorData.location?.coordinates && (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Navigation />}
                    onClick={openInGoogleMaps}
                    sx={{
                      mt: 2,
                      borderColor: '#1a7f3a',
                      color: '#1a7f3a',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: '#E8F5E9',
                      },
                    }}
                  >
                    Open in Google Maps
                  </Button>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* ================= DEALS GRID ================= */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)'
            },
            gap: isMobile ? 0 : 2,
            mb: 4
          }}
        >
          {dealsWithDistance.slice(0, 12).map((deal, index) => {
            const dealId = deal.id || deal._id;
            const isLoadingFav = favoritesLoading[dealId];

            // Safely get image URL
            const rawImageUrl = deal.deal_url || deal.image_url || deal.img_url;
            const finalImageUrl = rawImageUrl 
              ? getImageUrl(rawImageUrl) 
              : 'https://via.placeholder.com/400x300?text=No+Image';

            return (
              <Card
                key={dealId}
                onClick={() => navigate(`/deal/${dealId}`)}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: isMobile
                    ? 'none'
                    : '0 2px 4px rgba(0,0,0,0.1)',
                  borderRadius: isMobile ? 0 : 1,
                  border: isMobile ? '2px solid #E5E7EB' : 'none',
                  borderRight: isMobile ? '2px solid #E5E7EB' : 'none',
                  borderBottom: isMobile ? '2px solid #E5E7EB' : 'none',
                  '&:hover': {
                    boxShadow: isMobile
                      ? 'none'
                      : '0 4px 12px rgba(0,0,0,0.15)',
                    transform: isMobile ? 'none' : 'translateY(-4px)',
                  }
                }}
              >
                {/* Image Container with Object-Fit Cover */}
                <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                  <CardMedia
                    component="img"
                    height={isMobile ? "140" : "180"}
                    image={finalImageUrl}
                    alt={deal.deal_name}
                    sx={{
                      objectFit: 'cover',
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  />

                  {/* Distance Chip */}
                  {deal.distance !== undefined && deal.distance !== null && (
                    <Chip
                      icon={<Navigation />}
                      label={`${deal.distance} km`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: isMobile ? 8 : 12,
                        left: isMobile ? 8 : 12,
                        fontWeight: 700,
                        bgcolor: '#1a7f3a', 
                        color: 'white',     
                        fontSize: isMobile ? '0.65rem' : '0.7rem',
                        height: isMobile ? 20 : 24,
                        backdropFilter: 'blur(10px)',
                        zIndex: 3,
                        '& .MuiChip-icon': {
                          color: 'white',   
                        },
                      }}
                    />
                  )}

                  {/* RED Discount Chip */}
                  <Chip
                    label={`${deal.percentage_discount}% OFF`}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: isMobile ? 8 : 12,
                      right: isMobile ? 8 : 12,
                      fontWeight: 700,
                      bgcolor: '#FF4444',
                      color: 'white',
                      fontSize: isMobile ? '0.65rem' : '0.7rem',
                      height: isMobile ? 20 : 24,
                      boxShadow: '0 4px 12px rgba(255,68,68,0.5)',
                      zIndex: 3,
                    }}
                  />
                </Box>

                {/* CONTENT */}
                <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                  {/* Category Chip */}
                  <Stack direction="row" spacing={1} sx={{ mb: isMobile ? 0.5 : 1 }}>
                    <Chip
                      label={deal.category || 'Offer'}
                      size="small"
                      sx={{
                        bgcolor: '#1a7f3a',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: isMobile ? '0.6rem' : '0.7rem',
                        height: isMobile ? 18 : 24,
                        width: '100%',
                        justifyContent: 'center',
                      }}
                    />
                  </Stack>

                  {/* Deal Title */}
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      mb: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: '#212121',
                      fontSize: isMobile ? '0.8rem' : '0.95rem',
                    }}
                  >
                    {deal.deal_name}
                  </Typography>

                  {/* Vendor Name */}
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                    <Store sx={{ fontSize: 12, color: '#878787' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#878787',
                        fontSize: isMobile ? '0.6rem' : '0.7rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {vendorData.business_name}
                    </Typography>
                  </Stack>

                  {/* Price */}
                  <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ mb: 0.5 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ 
                        color: '#1a7f3a',
                        fontSize: isMobile ? '0.85rem' : '1rem',
                      }}
                    >
                      ₹{deal.discounted_price?.toFixed(0)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        textDecoration: 'line-through',
                        color: '#878787',
                        fontSize: isMobile ? '0.65rem' : '0.75rem',
                      }}
                    >
                      ₹{deal.original_price?.toFixed(0)}
                    </Typography>
                  </Stack>

                  {/* Action Buttons */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: isMobile ? 1 : 1.5 }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      onClick={(e) => !isLoadingFav && handleToggleFavorite(dealId, e)}
                      sx={{ 
                        cursor: favoritesLoading[dealId] ? 'default' : 'pointer',
                        transition: 'all 0.3s ease', 
                        '&:hover': { opacity: favoritesLoading[dealId] ? 1 : 0.7 },
                        minWidth: '60px',
                      }}
                    >
                      {favoritesLoading[dealId] ? (
                        <CircularProgress size={isMobile ? 16 : 20} sx={{ color: '#1a7f3a' }} />
                      ) : isFavorite(dealId) ? (
                        <Favorite fontSize="small" sx={{ color: '#FF6B6B', fontSize: isMobile ? 16 : 20 }} />
                      ) : (
                        <FavoriteBorder fontSize="small" sx={{ color: 'text.secondary', fontSize: isMobile ? 16 : 20 }} />
                      )}
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}
                      >
                        {favoritesLoading[dealId] ? 'Saving...' : isFavorite(dealId) ? 'Saved' : 'Save'}
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/deal/${dealId}`);
                      }}
                      sx={{
                        borderColor: '#1a7f3a',
                        color: '#1a7f3a',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: isMobile ? '0.65rem' : '0.8rem',
                        py: isMobile ? 0.4 : 0.75,
                        minHeight: isMobile ? 28 : 36,
                        px: isMobile ? 1 : 2,
                        '&:hover': {
                          borderColor: '#1a7f3a',
                          bgcolor: '#E8F5E9',
                        },
                      }}
                    >
                      View
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* ================= EMPTY ================= */}
        {dealsWithDistance.length === 0 && (
          <Paper sx={{ p: 8, textAlign: 'center' }}>
            <Star sx={{ fontSize: 64, opacity: 0.3 }} />
            <Typography>No active deals</Typography>
          </Paper>
        )}
      </Container>

      {/* ================= SNACKBAR ================= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorDetails;